import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "sonner";
import { LoadingScreen } from "@semo/shared-ui";
import { EmbedAuthProvider } from "./features/auth";
import { ChatRealtimeLoader } from "./components/ChatRealtimeLoader";
import { VendorEntryFallback } from "./features/entry/VendorEntryFallback";

/**
 * 화면은 열릴 때 받는다.
 *
 * 전부 정적 import 였을 때 프로덕션 빌드가 청크 하나에 3.4 MB(gzip 949 KB)로 나왔다.
 * 공급사는 보통 링크 하나로 한 화면만 열러 오는데(알림톡 → /requests/:id 등),
 * 그 한 화면을 보려고 대시보드·채팅·원가계산서·바코드까지 전부 받고 있었다.
 */
const QuoteResponseEmbedPage = lazy(() =>
  import("./pages/QuoteResponseEmbedPage").then((m) => ({
    default: m.QuoteResponseEmbedPage,
  })),
);
const ChatEmbedPage = lazy(() =>
  import("./features/chat").then((m) => ({ default: m.ChatEmbedPage })),
);
const InventoryEmbedPage = lazy(() =>
  import("./pages/InventoryEmbedPage").then((m) => ({
    default: m.InventoryEmbedPage,
  })),
);
const AutoQuoteEmbedPage = lazy(() =>
  import("./pages/AutoQuoteEmbedPage").then((m) => ({
    default: m.AutoQuoteEmbedPage,
  })),
);
const ShareLinksEmbedPage = lazy(() =>
  import("./pages/ShareLinksEmbedPage").then((m) => ({
    default: m.ShareLinksEmbedPage,
  })),
);
const BarcodeScanEmbedPage = lazy(() =>
  import("./pages/BarcodeScanEmbedPage").then((m) => ({
    default: m.BarcodeScanEmbedPage,
  })),
);
// 글로벌씨마켓 — 후보 기업(세모 공급사, 한국어) 받은 제안 화면
const GcmOffersRoutes = lazy(() =>
  import("./features/gcm-deals").then((m) => ({ default: m.GcmOffersRoutes })),
);
const DevCostEstimatePreview = lazy(() =>
  import("./pages/DevCostEstimatePreview").then((m) => ({
    default: m.DevCostEstimatePreview,
  })),
);
const DevChatListPreview = lazy(() =>
  import("./pages/DevChatListPreview").then((m) => ({
    default: m.DevChatListPreview,
  })),
);
const DevAutoQuotePricePreview = lazy(() =>
  import("./pages/DevAutoQuotePricePreview").then((m) => ({
    default: m.DevAutoQuotePricePreview,
  })),
);
const DevProcureLinkQuotePreview = lazy(() =>
  import("./pages/DevProcureLinkQuotePreview").then((m) => ({
    default: m.DevProcureLinkQuotePreview,
  })),
);
const DevSettlementCalendarPreview = lazy(() =>
  import("./pages/DevSettlementCalendarPreview").then((m) => ({
    default: m.DevSettlementCalendarPreview,
  })),
);

/**
 * 화면 청크를 받는 동안 보여 줄 것. 빈 화면보다 낫다.
 * 공급사가 씨마켓에서 «물품 관리 시스템» 을 열면 제일 먼저 만나는 화면이라, 회색 스피너 대신
 * 세모 로고가 한가운데서 돈다(2026-09-27 대표 지시). 로그인 확인 로딩과 같은 자리·크기라
 * 둘이 이어서 떠도 한 번의 로딩으로 보인다.
 */
function RouteFallback() {
  return <LoadingScreen className="bg-gray-50" message="화면을 불러오는 중" />;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster richColors position="top-right" />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* =========================================================
            DEV-ONLY PREVIEW (로그인 없이 원가계산서 탭 미리보기)
            import.meta.env.DEV 에서만 존재 → 프로덕션 빌드 미포함
            ========================================================= */}
          {import.meta.env.DEV && (
            <Route path="/dev/preview" element={<DevCostEstimatePreview />} />
          )}
          {import.meta.env.DEV && (
            <Route path="/dev/chat-preview" element={<DevChatListPreview />} />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/auto-quote-preview"
              element={<DevAutoQuotePricePreview />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/settlement-calendar"
              element={<DevSettlementCalendarPreview />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/procure-link-quote"
              element={<DevProcureLinkQuotePreview />}
            />
          )}

          {/* =========================================================
            EMBED ROUTES (URL Token Auth)
            ========================================================= */}
          <Route
            element={
              <EmbedAuthProvider>
                <ChatRealtimeLoader />
                <Outlet />
              </EmbedAuthProvider>
            }
          >
            {/* Quote Request / Response (AlimTalk Link: /requests/:id) */}
            <Route
              path="/requests/:quoteRequestId"
              element={<QuoteResponseEmbedPage />}
            />
            <Route
              path="/embed/quote-response/:quoteRequestId"
              element={<QuoteResponseEmbedPage />}
            />
            <Route
              path="/quote-response/:quoteRequestId"
              element={<QuoteResponseEmbedPage />}
            />
            <Route
              path="/quote/:quoteRequestId"
              element={<QuoteResponseEmbedPage />}
            />

            {/* Contract Detail (AlimTalk Link: /contracts/:id) - Opens Dashboard > Contracts Tab > Detail Modal */}
            <Route
              path="/contracts/:contractId"
              element={<InventoryEmbedPage initialTab="contracts" />}
            />

            {/* Settlements (AlimTalk Link: /settlements) - Opens Dashboard > Settlements Tab */}
            <Route
              path="/settlements"
              element={<InventoryEmbedPage initialTab="settlements" />}
            />

            {/* Chat (AlimTalk Link: /chat/:roomId) */}
            <Route path="/embed/chat/:chatRoomId" element={<ChatEmbedPage />} />
            <Route path="/chat/:chatRoomId" element={<ChatEmbedPage />} />

            {/* 2026-07-31: c-market 참여현황 [견적서] → 대시보드 위 상세 모달 자동 오픈.
               InventoryEmbedPage 가 requestId 를 감지해 detailFromEmbed 플래그로
               발주처 정보 노출/1:1 채팅 버튼 숨김 처리를 적용한다. */}
            <Route
              path="/embed/quote-detail/:requestId"
              element={<InventoryEmbedPage />}
            />
            <Route
              path="/quote-detail/:requestId"
              element={<InventoryEmbedPage />}
            />

            <Route path="/embed/dashboard" element={<InventoryEmbedPage />} />
            <Route path="/auto-quote" element={<AutoQuoteEmbedPage />} />
            <Route path="/embed/auto-quote" element={<AutoQuoteEmbedPage />} />
            {/* 2026-09-08: 즉시견적 링크 관리 (물품관리 3물결 05) */}
            <Route path="/share-links" element={<ShareLinksEmbedPage />} />
            <Route
              path="/embed/share-links"
              element={<ShareLinksEmbedPage />}
            />
            {/* 2026-09-08: 바코드 스캔 → 내 품목 연결 (물품관리 3물결 03) */}
            <Route path="/scan" element={<BarcodeScanEmbedPage />} />
            <Route path="/embed/scan" element={<BarcodeScanEmbedPage />} />
            {/* 글로벌씨마켓 — 내 참여(세모 공급사 로그인) */}
            <Route path="/gcm-offers/*" element={<GcmOffersRoutes />} />
          </Route>

          {/* Fallback — 이 앱은 씨마켓 임베드(URL 토큰)로만 열린다. 주소로 들어온 공급기업에게 로그인·가입 길 안내 */}
          <Route path="*" element={<VendorEntryFallback />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
